import React, { useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"

import { EDIT, IDLE, PENDING } from "const"
import {
    SilDeviceNested,
    SilProjectDevice,
    SilProjectDeviceNested,
} from "server/models"
import { StateType, hasAccess, hasProjectAccess } from "client/utils"
import {
    PRM_SIL_DEVICE,
    StoreType,
    SilDeviceProps,
    SilDeviceNestedType,
    SilDeviceType,

    LoadSilDeviceById,
    CalculateSubsystem,

    setSilUpdateDeviceTargetSubsystem,
    setSilUpdateDeviceOriginSubsystem,
} from "client/store"

import {
    Container,
    PageHeader,
    PageTitle,
    PageContent,
    Loader,
} from "client/components"

import { getSilMode } from "client/pages/sil/formulas"

import NotFound from "client/parts/notFound"
import AccessDenied from "client/parts/accessDenied"
import Form from "./form"

import CreateOriginQidDevice from "../originQidDevice"

const SilUpdateSubsystem = ({
    deviceId = null,
}: SilDeviceProps) => {
    const targetDevice = useSelector((state: StoreType) => state.silUpdate.subsystem.targetDevice)
    const project = useSelector((state: StoreType) => state.project.projectView)
    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()

    const [loadState, setLoadState] = useState<StateType>(IDLE)
    const [disableSave, setDisableSave] = useState(false)

    useEffect(() => {
        setLoadState(PENDING)
        LoadSilDeviceById(deviceId, userData.accountId, project.id).then((subsystem) => {
            // target device
            dispatch(setSilUpdateDeviceTargetSubsystem(subsystem))


            // check if the subsystem is CREATED on account or project level
            const projectSubsystem = subsystem as SilProjectDevice
            if (!projectSubsystem.deviceId) {
                // for subsystems created on account and project levels model is unchanged, but details of nested devices can be updated
                // load new data for the nested devices
                const promiseArray: Promise<any>[] = []
                const originNestedDevices: SilDeviceNestedType[] = []

                subsystem.nestedDevices.forEach((nested, index) => {
                    const projectDeviceNested = nested as SilProjectDeviceNested
                    if (projectDeviceNested.projectDeviceId) { // nested device is created on project level
                        promiseArray.push(
                            LoadSilDeviceById(projectDeviceNested.projectDeviceId, userData.accountId, project.id)
                            .then((device) => {
                                if (device) {
                                    const projectDevice = device as SilProjectDevice
                                    const newNested: SilProjectDeviceNested = {
                                        id: null,
                                        parentDeviceId: null,
                                        deviceId: null,
                                        projectDeviceId: projectDevice.id,

                                        qidProductId: projectDevice.qidProductId,
                                        qidModelId: projectDevice.qidModelId,
                                        qidSafetyFunctionId: projectDevice.qidSafetyFunctionId,
                                        qidOptionId: projectDevice.qidOptionId,
                                        qidDatapointId: projectDevice.qidDatapointId,

                                        qidModelName: projectDevice.qidModelName,
                                        qidSafetyFunctionName: projectDevice.qidSafetyFunctionName,
                                        qidOptionName: projectDevice.qidOptionName,
                                        qidDatapointDate: projectDevice.qidDatapointDate,
                                        qidMethod: projectDevice.qidMethod,

                                        manufacturer: projectDevice.manufacturer,
                                        name: projectDevice.name,
                                        logoId: projectDevice.logoId,
                                        logo: projectDevice.logo,
                                        type: projectDevice.type,
                                        mode: getSilMode(projectDevice),
                                        logicSolver: projectDevice.logicSolver,
                                        provenInUse: projectDevice.provenInUse,

                                        sd: projectDevice.sd,
                                        su: projectDevice.su,
                                        dd: projectDevice.dd,
                                        du: projectDevice.du,

                                        order: nested.order,
                                    }
                                    originNestedDevices[index] = newNested
                                } else {
                                    originNestedDevices[index] = nested
                                }
                            }),
                        )
                    } else if (nested.deviceId) { // nested device is created on account level
                        promiseArray.push(
                            LoadSilDeviceById(nested.deviceId, userData.accountId, null)
                            .then((device) => {
                                if (device) {
                                    const newNested: SilDeviceNested = {
                                        id: null,
                                        parentDeviceId: null,
                                        deviceId: device.id,

                                        qidProductId: null,
                                        qidModelId: null,
                                        qidSafetyFunctionId: null,
                                        qidOptionId: null,
                                        qidDatapointId: null,

                                        qidModelName: null,
                                        qidSafetyFunctionName: null,
                                        qidOptionName: null,
                                        qidDatapointDate: null,
                                        qidMethod: null,

                                        manufacturer: device.manufacturer,
                                        name: device.name,
                                        logoId: device.logoId,
                                        logo: device.logo,
                                        type: device.type,
                                        mode: getSilMode(device),
                                        logicSolver: device.logicSolver,
                                        provenInUse: device.provenInUse,

                                        sd: device.sd,
                                        su: device.su,
                                        dd: device.dd,
                                        du: device.du,

                                        order: nested.order,
                                    }

                                    const newProjectNested: SilProjectDeviceNested = {
                                        ...newNested,
                                        projectDeviceId: null,
                                    }

                                    if (projectSubsystem.projectId) originNestedDevices[index] = newProjectNested
                                    else originNestedDevices[index] = newNested
                                } else {
                                    originNestedDevices[index] = nested
                                }
                            }),
                        )
                    } else if (nested.qidProductId) {
                        promiseArray.push(
                            CreateOriginQidDevice(
                                nested.qidProductId,
                                nested.qidModelId,
                                nested.qidSafetyFunctionId,
                                nested.qidOptionId,
                                nested.qidDatapointId,
                                nested.qidMethod,
                            ).then((res) => {
                                if (projectSubsystem.projectId) {
                                    originNestedDevices[index] = res.projectNestedDevice
                                        ? {
                                            ...res.projectNestedDevice,
                                            order: nested.order,
                                        }
                                        : nested
                                } else {
                                    originNestedDevices[index] = res.nestedDevice
                                        ? {
                                            ...res.nestedDevice,
                                            order: nested.order,
                                        }
                                        : nested
                                }
                            }),
                        )
                    }
                })

                Promise.all(promiseArray).then(() => {
                    // calculate new subsystem data based on updated nested devices
                    const calculatedResult = CalculateSubsystem(subsystem, originNestedDevices)

                    // origin device
                    dispatch(setSilUpdateDeviceOriginSubsystem({
                        ...calculatedResult.calculatedSubsystem,
                        nestedDevices: originNestedDevices,
                    } as SilDeviceType))

                    setDisableSave(calculatedResult.disableSave)
                    setLoadState(IDLE)
                })
            } else {
                // load origin subsystem from account level
                LoadSilDeviceById(projectSubsystem.deviceId, userData.accountId, null).then((originSubsystem) => {
                    // origin device
                    dispatch(setSilUpdateDeviceOriginSubsystem(originSubsystem))
                    setLoadState(IDLE)
                })
            }
        })
    }, [])

    const editAccess = !project.id
        ? hasAccess(PRM_SIL_DEVICE, EDIT)
        : hasProjectAccess(PRM_SIL_DEVICE, EDIT)

    if (!editAccess) return <AccessDenied />
    if (!targetDevice) return <NotFound />

    return (
        <Container>
            <PageHeader>
                <PageTitle>{`${targetDevice.name} ${lang.details}`}</PageTitle>
            </PageHeader>
            <PageContent>
                {
                    loadState === PENDING
                    && <Loader />
                }
                <Form disableUpdateModel={disableSave} />
            </PageContent>
        </Container>
    )
}
export default SilUpdateSubsystem
